import React, { useState, ChangeEvent, SubmitEvent } from "react";
import {
  Container,
  Card,
  Form,
  Button,
  Row,
  Col,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { RiInformationFill } from "react-icons/ri";
import { BiHide, BiShow } from "react-icons/bi";

import Logo from "../../img/Logo.svg";
import Footer from "../Footer";
import { Clinic } from "../../services/getFunctions";
import { LoginConfig } from "./loginConfigs";

interface SharedLoginProps {
  config: LoginConfig;
  clinics: Clinic[];
  onClinicIdChange: (event: ChangeEvent<HTMLSelectElement>) => void;
  onUsernameChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onPasswordChange: (event: ChangeEvent<HTMLInputElement>) => void;
  onSubmit: (event: SubmitEvent<HTMLFormElement>) => void;
}

/**
 * ShareLogin component
 *
 * responsibility: only for UI display for patient, clinician and admin, rendering and state management
 * the logic, data management and navigation is handled by `useLoginState` hook.
 */
const SharedLogin = ({
  config,
  clinics,
  onClinicIdChange,
  onUsernameChange,
  onPasswordChange,
  onSubmit,
}: SharedLoginProps) => {
  const [hiddenPw, setHiddenPw] = useState(true);

  /**
   * Change hiddenPw state on true or false.
   */
  const togglePasswordVisibility = () => {
    setHiddenPw((pre) => !pre);
  };

  /**
   * Renders the user ID input form group
   *
   * This element contains from label, input control and invalid feedback
   * The tooltip for patient is conditional
   * It's defined as a controlled component
   * @returns {JSX.Element} A React element representing the User ID input form group.
   */
  const renderUserIdForm = () => {
    return (
      <Form.Group controlId="formGroupCode" className="mb-2">
        <Form.Label className="login-label">
          {config.userLabel}
          {config.userType === "patient" && (
            <OverlayTrigger
              overlay={
                <Tooltip className="tooltip" role="tooltip">
                  Bitte verwenden Sie die Ihnen bei der Anmeldung zugewiesene
                  SysDok-ID.
                </Tooltip>
              }
            >
              <span className="mdi">
                <RiInformationFill />
              </span>
            </OverlayTrigger>
          )}
        </Form.Label>

        <Form.Control
          aria-describedby={`tooltip${config.userLabel}`}
          className="form-control"
          required
          type="username"
          placeholder={config.userLabel}
          onChange={onUsernameChange}
        />
        <Form.Control.Feedback
          type="invalid"
          role="tooltip"
          id={`tooltip${config.userLabel}`}
          className="mb-n1"
        >
          {config.userIdFeedback}
        </Form.Control.Feedback>
      </Form.Group>
    );
  };

  /**
   * Readers the password visibility toggle button
   * Includes ARIA labels for accessibility.
   * @returns {JSX.Element} The password toggle button component
   */
  const renderToggleButton = () => {
    return (
      <Button
        className={"passwordShow p-0 ms-2"}
        variant=""
        onClick={togglePasswordVisibility}
      >
        {hiddenPw ? (
          <BiShow className="hideShowPw" aria-label="Passwort einblenden" />
        ) : (
          <BiHide className="hideShowPw" aria-label="Passwort ausblenden" />
        )}
      </Button>
    );
  };

  /**
   * Renders the password input control with dynamic type
   * a form control that switches between password and text type based on the hiddenPw state.
   * @returns {JSX.Element} The password input control component
   */
  const renderToggleControl = () => {
    return (
      <Form.Control
        aria-describedby={
          config.userType === "patient" ? "tooltipBirthday" : ""
        }
        className="form-control"
        required
        type={hiddenPw ? "password" : "text"}
        placeholder={config.userType === "patient" ? "tt.mm.jjjj" : "Passwort"}
        onChange={onPasswordChange}
      />
    );
  };

  /**
   * Renders the "forget password" field with tooltip
   * - Creates a link button with a tooltip explaining the password recovery process.
   * - Includes hidden text for accessibility purposes.
   * @returns {JSX.Element} The forgot password field component
   */
  const renderForgotPasswordField = () => {
    return (
      <>
        <OverlayTrigger
          overlay={
            <Tooltip className="tooltip" role="tooltip">
              {config.forgotPasswordTooltip}
            </Tooltip>
          }
        >
          <Button aria-describedby="tooltipLink" variant="link">
            {config.forgetLinkText}
          </Button>
        </OverlayTrigger>
        {/**hidden text for accessibility */}
        <div hidden id="tooltipLink">
          {config.forgotPasswordTooltip}
        </div>
      </>
    );
  };

  return (
    <Container className="d-flex justify-content-center align-items-center min-vh-100 pb-5">
      <Card className="col-lg-6 col-md-10 p-3">
        {/* WarningBanner component */}
        <div className="alert alert-warning me-2 text-center" role="alert">
          Bitte stellen Sie sicher, dass sie keine Übersetzungsplugins für diese
          Seite nutzen
        </div>

        {/**Logo Section*/}
        <Card.Header className="card-header bg-transparent d-flex justify-content-center">
          <img src={Logo} alt="Logo" width="230px" height="auto" />
        </Card.Header>

        {/**Form Body */}
        <Card.Body className="p-5">
          <Card.Title className="text-dark mb-4">{config.title}</Card.Title>
          <Form noValidate validated onSubmit={onSubmit}>
            {/**Clinic Field */}
            <Form.Group controlId="formGroupClinic" className="mb-2">
              <Form.Label>
                Ambulanz
                <OverlayTrigger
                  overlay={
                    <Tooltip role="tooltip">
                      Ihre Ambulanz ist in der Liste nicht enthalten? Wenden Sie
                      sich bitte an sysdok@uniklinik-freiburg.de
                    </Tooltip>
                  }
                >
                  <span className="ms-2">
                    <RiInformationFill />
                  </span>
                </OverlayTrigger>
              </Form.Label>
              <Form.Select
                aria-describedby="tooltipClinic"
                className="form-control"
                required
                defaultValue=""
                onChange={onClinicIdChange}
              >
                {clinics &&
                  clinics.length > 0 &&
                  [
                    {
                      name: "Bitte auswählen",
                      id: "",
                    },
                    ...clinics,
                  ].map((clinic, index) => {
                    return (
                      <option key={index} value={clinic.id}>
                        {clinic.name}
                      </option>
                    );
                  })}
              </Form.Select>
            </Form.Group>

            {/**user ID field */}
            {renderUserIdForm()}

            {/**Password field */}
            <Form.Group controlId="formGroupPassword">
              <Row>
                <Col sm="3" xs="5">
                  <div className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0">
                      {config.passwordLabel}
                    </Form.Label>
                    {renderToggleButton()}
                  </div>
                </Col>
              </Row>
              {renderToggleControl()}
              {config.userType === "patient" && (
                <Form.Text role="tooltip" id="tooltipBirthday">
                  Beispiel: Geburtstag am 7. September 1984. Eingabe: 07.09.1984
                </Form.Text>
              )}
            </Form.Group>

            {/**Forget password link */}
            {renderForgotPasswordField()}

            {/**login button */}
            <div className="d-grid gap-2">
              <Button variant="primary" type="submit">
                Anmelden
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>

      <Footer>{config.userType}</Footer>
    </Container>
  );
};

export default SharedLogin;
